import React, { useCallback, useEffect, useState, useMemo } from "react";
import { QRCodeSVG } from "qrcode.react";
import * as jose from "jose";
import { useNavigate } from "react-router-dom";

const signingKey = import.meta.env.VITE_SIGNING_KEY;
const qrCodeRefreshInterval =
  import.meta.env.VITE_QR_CODE_REFRESH_INTERVAL || "30s";
const qrCodeAccessKey = import.meta.env.VITE_QR_CODE_ACCESS_KEY;

// 有効期限文字列をミリ秒に変換
const parseDuration = (duration: string): number => {
  const match = duration.match(/^(\d+)([smh])$/);
  if (!match) return 2 * 60 * 1000; // デフォルト: 2分

  const value = parseInt(match[1], 10);
  const unit = match[2];

  switch (unit) {
    case "s":
      return value * 1000;
    case "m":
      return value * 60 * 1000;
    case "h":
      return value * 60 * 60 * 1000;
    default:
      return 2 * 60 * 1000;
  }
};

export const ChanelQrCodePage: React.FC = () => {
  const navigate = useNavigate();
  const [qrUrl, setQrUrl] = useState<string>("");
  const [remainingTime, setRemainingTime] = useState<number>(0);
  const [isAuthorized, setIsAuthorized] = useState<boolean>(false);

  // QRコード更新間隔をミリ秒で取得
  const refreshInterval = useMemo(
    () => parseDuration(qrCodeRefreshInterval),
    []
  );

  // アクセスキーの検証
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const key = urlParams.get("key");

    if (key === qrCodeAccessKey) {
      setIsAuthorized(true);
    } else {
      // 認証失敗時はNotFoundページへリダイレクト
      navigate("/not-found", { replace: true });
    }
  }, [navigate]);

  // JWT形式のトークンを生成（有効期限 = QRコード更新間隔）
  const generateAccessToken = useCallback(async (): Promise<string> => {
    const encodedSecret = new TextEncoder().encode(signingKey);
    return await new jose.SignJWT({ access_granted: true })
      .setProtectedHeader({ alg: "HS256" })
      .setExpirationTime(qrCodeRefreshInterval)
      .setIssuedAt()
      .sign(encodedSecret);
  }, []);

  // QRコード用のURLを生成
  const generateQrUrl = useCallback(async (): Promise<string> => {
    const accessToken = await generateAccessToken();
    const url = new URL("/entry", window.location.origin);
    url.searchParams.set("access_token", accessToken);
    return url.toString();
  }, [generateAccessToken]);

  // QRコードを更新
  const updateQrCode = useCallback(async () => {
    const url = await generateQrUrl();
    setQrUrl(url);
    setRemainingTime(Math.floor(refreshInterval / 1000));
  }, [generateQrUrl, refreshInterval]);

  // 初回とインターバルでQRコードを更新
  useEffect(() => {
    if (!isAuthorized) return;

    // 初回更新
    updateQrCode();

    // 定期更新
    const intervalId = setInterval(() => {
      updateQrCode();
    }, refreshInterval);

    return () => clearInterval(intervalId);
  }, [isAuthorized, updateQrCode, refreshInterval]);

  // 残り時間のカウントダウン
  useEffect(() => {
    if (!isAuthorized || remainingTime <= 0) return;

    const timerId = setInterval(() => {
      setRemainingTime((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);

    return () => clearInterval(timerId);
  }, [isAuthorized, remainingTime]);

  // 認証前は何も表示しない
  if (!isAuthorized) {
    return null;
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-white p-4">
      <div className="bg-white p-6 rounded-lg shadow-lg border border-gray-200">
        {qrUrl ? (
          <QRCodeSVG
            value={qrUrl}
            size={256}
            level="M"
            includeMargin={true}
          />
        ) : (
          <div className="w-64 h-64 flex items-center justify-center bg-gray-100">
            <span className="text-gray-500">読み込み中...</span>
          </div>
        )}
      </div>
    </div>
  );
};
